import {
  assignWinnersSlugs,
  getWinnersShowNames,
  type WinnersSlugSource,
} from "./winners";

export const PREDICTIONS_INDEX_PATH = "/predictions";

export function predictionShowPath(slug: string): string {
  return `${PREDICTIONS_INDEX_PATH}/${slug}`;
}

export function predictionSearchLabel(show: {
  name: string;
  year: number;
}): string {
  return `${show.year} ${getWinnersShowNames(show.name).displayName}`;
}

/**
 * A prediction URL may be the public slug (`oscars-2026`) or the show id
 * from an older share link. Both resolve to the same show.
 */
export function resolvePredictionShowParam(
  param: string,
  shows: readonly WinnersSlugSource[],
): { id: string; slug: string } | null {
  const trimmed = param.trim();
  if (!trimmed) {
    return null;
  }

  const assigned = assignWinnersSlugs(shows);
  const byId = assigned.get(trimmed);
  if (byId) {
    return { id: trimmed, slug: byId };
  }

  for (const [id, slug] of assigned) {
    if (slug === trimmed) {
      return { id, slug };
    }
  }

  return null;
}

export function getPredictionsIndexKeywords(): string[] {
  return [
    "award show predictions",
    "oscar predictions",
    "emmy predictions",
    "grammy predictions",
    "golden globe predictions",
    "who will win the oscars",
    "live award show predictions",
  ];
}

export function getPredictionsShowKeywords(input: {
  displayName: string;
  officialName: string;
  year: number;
}): string[] {
  const { displayName, officialName, year } = input;
  const candidates = [
    `${year} ${displayName} predictions`,
    `${displayName} predictions ${year}`,
    `${officialName} ${year} predictions`,
    `who will win the ${year} ${displayName}`,
    `${year} ${displayName} winner predictions`,
    `${displayName} predictions`,
  ];
  const seen = new Set<string>();
  const keywords: string[] = [];

  for (const keyword of candidates) {
    const key = keyword.toLowerCase();
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    keywords.push(keyword);
  }

  return keywords;
}

export function joinSearchList(
  labels: readonly string[],
  locale = "en",
): string {
  const items = labels.map((label) => label.trim()).filter(Boolean);
  if (items.length === 0) {
    return "";
  }
  return new Intl.ListFormat(locale, {
    style: "long",
    type: "conjunction",
  }).format(items);
}

export function predictionCategoryListName(category: {
  name: string;
  nominees: ReadonlyArray<{ name: string; isPick: boolean }>;
}): string {
  const picks = category.nominees
    .filter((nominee) => nominee.isPick)
    .map((nominee) => nominee.name.trim())
    .filter(Boolean);
  if (picks.length === 0) {
    return category.name;
  }
  return `${category.name}: ${picks.join(", ")}`;
}
